import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Search, ArrowRight } from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';

const FAQS = [
  {
    question: 'What is Levanta?',
    answer:
      'Levanta is a structured personal growth platform built around discipline, structure, and community.\nIt is designed for long-term development, not short-term motivation.',
  },
  {
    question: 'Is Levanta a course?',
    answer:
      'No.\n\nLevanta is not a one-time course with a start and finish date.\nIt is an ongoing system where members progress through structured areas of growth over time.',
  },
  {
    question: 'Is Levanta therapy or coaching?',
    answer:
      'No.\n\nLevanta is not therapy and does not replace professional mental health support.\nIt is a structured self-development system focused on skills, discipline, and personal responsibility.',
  },
  {
    question: 'How is Levanta different from other self-development platforms?',
    answer:
      'Most platforms provide content.\n\nLevanta provides structure.\n\nInstead of random inspiration, members follow a clear progression system with defined domains, skill paths, and long-term development.',
  },
  {
    question: 'Who is Levanta for?',
    answer:
      'Levanta is for individuals who:\n\n• Want structure in their growth\n• Value discipline over motivation\n• Think long-term\n• Are willing to take responsibility',
  },
  {
    question: 'Who is Levanta not for?',
    answer:
      'Levanta is not for:\n\n• People looking for quick fixes\n• Passive content consumers\n• Those unwilling to commit to consistent effort',
  },
  {
    question: 'How much time does Levanta require?',
    answer:
      'Levanta is designed to fit into real life.\n\nMembers are encouraged to build consistent habits rather than intense short bursts. Progress is steady and long-term.',
  },
  {
    question: 'Is there a community?',
    answer:
      'Yes.\n\nLevanta is built around the concept of community — committed individuals progressing together through shared standards and structure.',
  },
  {
    question: 'Can I cancel anytime?',
    answer:
      'Membership terms depend on the plan selected.\nDetails are provided during the registration process.',
  },
  {
    question: 'What is the long-term vision of Levanta?',
    answer:
      'Levanta aims to build a global structured growth system — expanding domains, strengthening community, and creating a long-term culture of discipline and capability.',
  },
  {
    question: 'Do I need prior experience in self-development to join?',
    answer:
      'No.\n\nLevanta is built for both beginners and experienced individuals.\nThe system provides structure regardless of your starting point.',
  },
  {
    question: 'Is Levanta only digital?',
    answer:
      'Levanta operates primarily through its digital platform, but the long-term vision includes real-world gatherings, expanded systems, and community initiatives.',
  },
  {
    question: 'How is progress measured?',
    answer:
      'Progress is structured through defined domains and skill paths.\nMembers can track advancement over time rather than relying on feelings or temporary motivation.',
  },
  {
    question: 'Is Levanta a motivational platform?',
    answer:
      'No.\n\nMotivation fades.\nLevanta focuses on discipline, structure, and consistent execution.',
  },
  {
    question: 'Is Levanta a network marketing company?',
    answer:
      'No.\n\nLevanta is a structured personal growth platform.\nIt is not built around recruitment or multi-level compensation systems.',
  },
  {
    question: 'Can teenagers join Levanta?',
    answer:
      'Levanta is designed primarily for adults who are ready to take responsibility for their growth.\nSpecific age requirements are outlined in the terms of service.',
  },
  {
    question: 'What makes the Community important?',
    answer:
      'Growth in isolation is fragile.\n\nThe Community provides accountability, shared standards, and long-term commitment.\nIt reinforces discipline when motivation drops.',
  },
  {
    question: 'What happens after I join?',
    answer:
      'Members gain access to the structured system, progression paths, and community.\nGrowth begins with foundation domains before advancing to higher levels.',
  },
  {
    question: 'How long does it take to see results?',
    answer:
      'Levanta is built for long-term development.\nResults depend on consistency and commitment, not speed.',
  },
  {
    question: 'Is Levanta constantly evolving?',
    answer:
      'Yes.\n\nLevanta is designed as a living system.\nNew domains, improvements, and expansions will be added over time as the platform grows.',
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [query, setQuery] = useState('');

  const filtered = FAQS.filter((f) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q);
  });

  const toggle = (idx) => setOpenIndex(openIndex === idx ? null : idx);

  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* HERO — same sky-gradient palette as the homepage so the transparent
          header has a colored backdrop to sit on at rest. */}
      <section className="relative pt-[110px] md:pt-[170px] pb-14 md:pb-28 px-6 overflow-hidden min-h-[580px] md:min-h-[760px]">
        <AltParallaxBackdrop />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7"
            data-testid="faq-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 inline-block" />
            Help Center
          </span>
          <h1
            className="text-4xl md:text-7xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] alt-hero-glow"
          >
            Frequently asked
            <br />
            <em className="italic font-light text-blue-600">questions.</em>
          </h1>
          <p className="mt-7 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            Everything there is to know about Levanta — what it is, who it&rsquo;s for,
            and how the system actually works.
          </p>

          {/* Search */}
          <div className="mt-10 max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#0b1f3a]/40" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the FAQ…"
                aria-label="Search the FAQ"
                data-testid="faq-search"
                className="w-full pl-14 pr-5 py-4 rounded-full bg-white/95 backdrop-blur-sm text-[#0b1f3a] placeholder:text-[#0b1f3a]/40 shadow-lg ring-1 ring-black/5 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ACCORDION */}
      <section className="px-6 py-20 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto">
          {filtered.length === 0 ? (
            <div className="mt-12 text-center py-16 px-6 rounded-2xl border border-dashed border-[#0b1f3a]/15 bg-[#f8fafc]">
              <p className="text-lg text-[#0b1f3a]/70">
                No questions matched <span className="font-semibold">&ldquo;{query}&rdquo;</span>.
              </p>
              <button
                type="button"
                onClick={() => setQuery('')}
                className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-sm"
              >
                Clear search
              </button>
            </div>
          ) : (
            <div className="mt-12 space-y-3">
              {filtered.map((faq, idx) => {
                const isOpen = openIndex === idx;
                return (
                  <div
                    key={faq.question}
                    className={`rounded-2xl ring-1 transition-all duration-200 ${
                      isOpen
                        ? 'bg-white ring-blue-200 shadow-[0_18px_45px_-15px_rgba(74,155,232,0.35)]'
                        : 'bg-[#fafbfd] ring-black/5 hover:ring-blue-200 hover:bg-white'
                    }`}
                    data-testid={`faq-item-${idx}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(idx)}
                      aria-expanded={isOpen}
                      className="w-full text-left flex items-center justify-between gap-4 px-6 md:px-7 py-5 md:py-6"
                    >
                      <h3 className="text-base md:text-lg font-semibold text-[#0b1f3a] leading-snug">
                        {faq.question}
                      </h3>
                      <span
                        className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                          isOpen
                            ? 'bg-blue-600 text-white rotate-180'
                            : 'bg-[#0b1f3a]/5 text-[#0b1f3a]'
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" strokeWidth={2.5} />
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-6 md:px-7 pb-6 md:pb-7 -mt-1">
                        <p className="text-[15px] md:text-base leading-relaxed text-[#0b1f3a]/75 whitespace-pre-line">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA band — matches the homepage's bottom CTA. */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-black leading-tight">
            Still have <em className="italic font-light text-blue-300">questions?</em>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-white/75">
            Send a message — every email reaches a real person on the team.
          </p>
          <div className="mt-10 flex justify-center">
            <Link
              to="/contact"
              data-testid="faq-cta-contact"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#0b1f3a] font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Contact the team
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default FAQ;
